import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Network, Wifi, Shield, Zap } from 'lucide-react';

const NewOfficeSetup = () => {
  const setupServices = [
    {
      icon: <Network className="w-8 h-8" />,
      title: 'Network Planning',
      description: 'Complete network architecture design for your new space',
    },
    {
      icon: <Wifi className="w-8 h-8" />,
      title: 'Wi-Fi Coverage',
      description: 'Enterprise-grade wireless network throughout your office',
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Security Setup',
      description: 'Comprehensive cybersecurity measures from day one',
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Quick Deployment',
      description: 'Fast, efficient setup to get you operational quickly',
    },
  ];

  const setupSteps = [
    {
      step: '1',
      title: 'Initial Consultation',
      description: 'We assess your space, business needs, and technology requirements',
    },
    {
      step: '2',
      title: 'Network Design',
      description: 'Custom IT infrastructure plan tailored to your office layout',
    },
    {
      step: '3',
      title: 'Installation',
      description: 'Professional setup of all network equipment and systems',
    },
    {
      step: '4',
      title: 'Testing & Training',
      description: 'Thorough testing and staff training to ensure smooth operations',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                New Office Setup Specialists
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Building a New Office?
                <span className="text-[#2995FF]"> We've Got You Covered</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                From network architecture to complete IT infrastructure setup, CyberTek handles everything for your new office build or expansion.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/request-quote"
                  className="px-8 py-4 bg-[#2995FF] text-white rounded-lg font-semibold hover:bg-[#004AAD] shadow-lg flex items-center justify-center space-x-2"
                >
                  <span>Get Started Today</span>
                  <ArrowRight size={20} />
                </Link>
                <Link
                  to="/contact"
                  className="px-8 py-4 bg-white text-[#2995FF] border-2 border-[#2995FF] rounded-lg font-semibold hover:bg-blue-50 flex items-center justify-center"
                >
                  Schedule Consultation
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/8867190/pexels-photo-8867190.jpeg"
                alt="Office Setup"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Complete New Office IT Setup</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need for a fully functional, secure office network
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {setupServices.map((service, index) => (
              <div
                key={index}
                className="p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center text-[#2995FF] mb-4">
                  {service.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-sm text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Setup Process */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#f2f2f2]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Setup Process</h2>
            <p className="text-xl text-gray-600">From planning to launch in four simple steps</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {setupSteps.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-6 shadow-lg h-full">
                  <div className="w-12 h-12 bg-[#2995FF] text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
                {index < setupSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="text-[#2995FF]" size={24} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/1148820/pexels-photo-1148820.jpeg"
                alt="Server Infrastructure"
                className="rounded-2xl shadow-xl"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">What's Included</h2>
              <p className="text-lg text-gray-600">
                Our comprehensive new office setup covers all aspects of IT infrastructure:
              </p>
              <div className="space-y-4">
                {[
                  'Complete network architecture and design',
                  'Structured cabling throughout office',
                  'Router, switch, and firewall installation',
                  'Enterprise Wi-Fi coverage',
                  'Server setup and configuration',
                  'Workstation deployment',
                  'Microsoft 365 setup and migration',
                  'Security and endpoint protection',
                  'VoIP phone system (optional)',
                  'Post-launch support and training',
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="text-[#2995FF] mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us for New Office Setup */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#f2f2f2]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose CyberTek?</h2>
            <p className="text-xl text-gray-600">Experience that makes a difference</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Proven Track Record',
                description: 'Successfully deployed dozens of new office networks',
              },
              {
                title: 'Contractor Coordination',
                description: 'We work seamlessly with your builders and architects',
              },
              {
                title: 'Scalable Solutions',
                description: 'Infrastructure designed to grow with your business',
              },
              {
                title: 'On-Time Delivery',
                description: 'We meet your move-in deadlines without compromise',
              },
              {
                title: 'Budget-Friendly',
                description: 'Enterprise solutions at affordable prices',
              },
              {
                title: 'Ongoing Support',
                description: 'Post-launch support to ensure smooth operations',
              },
            ].map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#2995FF] to-[#004AAD]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Ready to Build Your Perfect Office Network?</h2>
          <p className="text-xl mb-8 text-blue-100">
            Let's discuss your new office IT needs. Schedule a free consultation to get started.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/request-quote"
              className="px-8 py-4 bg-white text-[#2995FF] rounded-lg font-semibold hover:bg-gray-100 shadow-lg"
            >
              Request a Quote
            </Link>
            <Link
              to="/contact"
              className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-[#2995FF]"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewOfficeSetup;
